import { intersection } from "io-ts";
import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { array } from "../../../shared/src/codecs/types/array";
import { boolean } from "../../../shared/src/codecs/types/boolean";
import { dateTime } from "../../../shared/src/codecs/types/dateTime";
import { form } from "../../../shared/src/codecs/types/form";
import { literal } from "../../../shared/src/codecs/types/literal";
import { nullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { required } from "../../../shared/src/codecs/types/required";
import { union } from "../../../shared/src/codecs/types/union";
import { uuid } from "../../../shared/src/codecs/types/uuid";
import { ChaseReasons, ChaseReasonsWithoutAwaitingOpenNewCase } from "../Cases";
import { EmptyObject } from "../EmptyObject";

export const ChaseStatus = union([
    literal("workable"),
    literal("chase"),
    literal("workable_rejected_chase_request"),
]);
export type TChaseStatusTypeCodec = typeof ChaseStatus;
export type TChaseStatusType = TTypeOfCodec<TChaseStatusTypeCodec>;

export const ChaseAcknowledgementType = union([
    literal("acknowledge"),
    literal("accept"),
    literal("reject")
]);

export const CaseChaseStatus = required({
    status: ChaseStatus,
});
export type TCaseChaseStatusCodec = typeof CaseChaseStatus;
export type TCaseChaseStatus = TTypeOfCodec<TCaseChaseStatusCodec>;

export const ChaseModeUpdateForm = form(
    required({
        id: uuid()
    }),
    EmptyObject,
);
export type TChaseModeUpdateFormCodec = typeof ChaseModeUpdateForm;
export type TChaseModeUpdateForm = TTypeOfCodec<TChaseModeUpdateFormCodec>;

// What carries from an aborted matter onto its successor. Clients and property are
// absent by design — they always transfer, because a change to either means this is a
// new instruction rather than the same transaction with a new buyer.
//
// Adding a group here means adding it in two hand-written places that TypeScript cannot
// check: the jsonb_build_object in GetCaseChaseModeController, which seeds the defaults
// in SQL, and the popout's useState. GetCaseChaseModeController's test reads the keys off
// this codec, so a missed SQL seed fails there by name.
export const MatterTransferGroups = required({
    documents: boolean(),
    charges: boolean(),
    quote: boolean(),
    searches: boolean(),
    bank_accounts: boolean(),
    pif: boolean(),
});
export type TMatterTransferGroupsCodec = typeof MatterTransferGroups;
export type TMatterTransferGroups = TTypeOfCodec<typeof MatterTransferGroups>;

// children carries the successor's id back so the CRM can land the caseworker on the new
// matter — the aborted one is finished, the successor is where the work now is.
export const CloseAwaitingNewCaseForm = form(
    required({
        id: uuid(),
        transfer: MatterTransferGroups,
    }),
    required({
        successor_case_id: union([uuid(), nullCodec()]),
    }),
);
export type TCloseAwaitingNewCaseFormCodec = typeof CloseAwaitingNewCaseForm;
export type TCloseAwaitingNewCaseForm = TTypeOfCodec<typeof CloseAwaitingNewCaseForm>;

export const ChaseModeUpdateFormWithChaseReasons = form(
    required({
        id: uuid(),
        selected_chase_reasons: array(ChaseReasons)
    }),
    EmptyObject,
);
export type TChaseModeUpdateFormWithChaseReasonsCodec = typeof ChaseModeUpdateFormWithChaseReasons;
export type TChaseModeUpdateFormWithChaseReasons = TTypeOfCodec<TChaseModeUpdateFormWithChaseReasonsCodec>;

export const CaseChaseMode = required({
    id: uuid(),
});

export const CaseChaseModeForm = 
    form(
        CaseChaseMode,
        required({
            accept_chase_transition_form: ChaseModeUpdateForm,
            acknowledge_workable_transition_form: ChaseModeUpdateForm,
            reject_from_chase_mode_form: ChaseModeUpdateForm,
            submit_to_workable_mode_form: ChaseModeUpdateForm,
            submit_to_chase_mode_form: ChaseModeUpdateFormWithChaseReasons,
            submit_to_closed_awaiting_new_case_form: CloseAwaitingNewCaseForm,
            submit_to_closed_unsuccessfully_form: ChaseModeUpdateForm,
            submit_to_closed_opened_in_error_form: ChaseModeUpdateForm,
            submit_to_reopen_form: ChaseModeUpdateForm,
            latest_chase: required(
                {
                    status: ChaseStatus,
                    // When the case entered its current chase spell — drives the banner's
                    // "In chase for N days" line (a workable→chase bounce writes a new row,
                    // so this is always the start of the spell it is currently in).
                    created_at: dateTime(),
                    acknowledged_at: union([nullCodec(), dateTime()]),
                    acknowledgement_type: union([ChaseAcknowledgementType, nullCodec()])
                }
            ),
            current_chase_reasons: array(ChaseReasons),
            available_chase_reasons: array(ChaseReasonsWithoutAwaitingOpenNewCase),
        })
    ); 
 
export type TCaseChaseModeFormCodec = typeof CaseChaseModeForm;
export type TCaseChaseModeForm = TTypeOfCodec<TCaseChaseModeFormCodec>;

export const CaseUpdateChaseModeForm = 
    form(
        required({
            case_id: uuid(),
            status: ChaseStatus,
            selected_chase_reasons: array(ChaseReasons),
        }),
        required({
            available_chase_reasons: array(ChaseReasonsWithoutAwaitingOpenNewCase),
        })
    ); 

export type TCaseUpdateChaseModeFormCodec = typeof CaseUpdateChaseModeForm;
export type TCaseUpdateChaseModeForm = TTypeOfCodec<TCaseUpdateChaseModeFormCodec>;